import { useInfiniteQuery } from '@tanstack/react-query'

import {
  companyCertificateActivityApi,
  type CompanyCertificateActivityParams,
} from 'services/companyCertificateActivityApi'

import { COMPANY_CERTIFICATE_ACTIVITY_QUERY_KEY } from './useCompanyCertificateActivity'

export const COMPANY_CERTIFICATE_ACTIVITY_PAGE_SIZE = 25

/**
 * Infinite-scroll variant of `useCompanyCertificateActivity`, for the full-page
 * Certificate activity view (VRG-430) — the same feed Home's Recent activity card
 * shows a fixed `limit: 6` slice of.
 *
 * Unlike `useCompanyActivityInfinite` (the admin audit log, page/pageSize), this
 * endpoint paginates by OFFSET/LIMIT (`companyCertificateActivityApi` / GET
 * /action-log/company — see its docblock). So the next page param is the next
 * OFFSET, not a page number: `getNextPageParam` returns `offset + data.length`
 * while that is still less than `total`, else `undefined` (no more pages). The
 * page flattens `data.pages.flatMap(p => p.data)`, mirroring
 * `useCertificateActivityInfinite` (the single-certificate tab), which paginates
 * the same way.
 *
 * `params` carries server-side narrowing (e.g. `actorUserId`) and is part of the
 * queryKey, so changing it resets the infinite query to offset 0.
 */
export const useCompanyCertificateActivityInfinite = (
  params?: Omit<CompanyCertificateActivityParams, 'limit' | 'offset'>
) =>
  useInfiniteQuery({
    queryKey: [...COMPANY_CERTIFICATE_ACTIVITY_QUERY_KEY, 'infinite', params],
    queryFn: ({ pageParam = 0 }) =>
      companyCertificateActivityApi.getCompanyCertificateActivity({
        ...params,
        limit: COMPANY_CERTIFICATE_ACTIVITY_PAGE_SIZE,
        offset: pageParam as number,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    staleTime: 60 * 1000,
  })
